import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, openSocket, type Room, type User, type WireMessage } from "../lib/api";
import { getEnrolledMethod, hasWrapped, methodLabel, rememberPsk, tapUnlock } from "../lib/unlock";
import { useIdleLock } from "../lib/useIdleLock";
import {
  createCipher,
  encryptFile,
  decryptFile,
  CIPHER_LABELS,
  pgp,
  type RoomCipher,
  type Envelope,
  type FileRef,
} from "../lib/crypto";
import { renderMarkdown } from "../lib/markdown";
import { PgpPanel } from "./PgpPanel";

interface Decrypted {
  wire: WireMessage;
  env?: Envelope; // present if decryption succeeded
}

export function RoomView({
  room,
  user,
  onRoomUpdate,
}: {
  room: Room;
  user: User;
  onRoomUpdate?: (room: Room) => void;
}) {
  const isInitiator = room.created_by === user.id;
  const [passphrase, setPassphrase] = useState("");
  const [cipher, setCipher] = useState<RoomCipher | null>(null);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState(false);
  const [handle, setHandle] = useState(() => localStorage.getItem(`wn_handle_${room.id}`) ?? user.username);
  const [messages, setMessages] = useState<Decrypted[]>([]);
  const [expiry, setExpiry] = useState<number | null>(room.expiry_seconds);
  const lastSeq = useRef<string | undefined>(undefined);
  const endRef = useRef<HTMLDivElement>(null);

  // Cache the unlock for the tab so a refresh or switching rooms doesn't
  // re-prompt. sessionStorage is per-tab and cleared when the tab closes; the
  // passphrase is never written to disk.
  const pskKey = `wn_psk_${room.id}`;
  const unlockScope = `room:${room.id}`;
  const [bindMsg, setBindMsg] = useState<string | null>(null);

  // Aggressive idle auto-lock; re-unlock is a tap (or the passphrase).
  const lock = useCallback(() => {
    sessionStorage.removeItem(pskKey);
    setCipher(null);
    setPassphrase("");
  }, [pskKey]);
  useIdleLock(!!cipher, lock);

  function deriveCipher(pass: string): Promise<RoomCipher> {
    return createCipher({
      mode: room.mode,
      roomId: room.id,
      saltB64: room.kdf_salt,
      passphrase: pass,
      isInitiator,
    });
  }

  // Reset on room change.
  useEffect(() => {
    setCipher(null);
    setMessages([]);
    setPassphrase("");
    setUnlockError(null);
    lastSeq.current = undefined;
  }, [room.id]);

  // Auto-unlock from a passphrase cached earlier this tab session.
  useEffect(() => {
    const saved = sessionStorage.getItem(pskKey);
    if (!saved) return;
    let cancelled = false;
    deriveCipher(saved)
      .then((c) => {
        if (!cancelled) {
          setCipher(c);
          setPassphrase(saved); // keep it available for "bind to key"
        }
      })
      .catch(() => sessionStorage.removeItem(pskKey));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.id]);

  async function unlock(e: React.FormEvent) {
    e.preventDefault();
    setUnlockError(null);
    try {
      setCipher(await deriveCipher(passphrase));
      sessionStorage.setItem(pskKey, passphrase); // remember for this tab
    } catch (err) {
      setUnlockError((err as Error).message);
    }
  }

  // Tap the security key to recover the wrapped passphrase and unlock.
  async function tapToUnlock() {
    setUnlockError(null);
    try {
      const p = await tapUnlock(unlockScope);
      setCipher(await deriveCipher(p));
      setPassphrase(p);
      sessionStorage.setItem(pskKey, p);
    } catch (err) {
      setUnlockError((err as Error).message);
    }
  }

  // Bind the active passphrase to the enrolled key (explicit gesture).
  async function bindKey() {
    try {
      await rememberPsk(unlockScope, passphrase);
      setBindMsg("bound ✓ — tap to unlock next time");
      alert("🔑 Bound to your key. Close/reopen the tab and you'll get a 'tap to unlock' button.");
    } catch (e) {
      setBindMsg(null);
      alert("Couldn't bind to key: " + (e as Error).message);
    }
  }

  async function decryptAll(wires: WireMessage[], c: RoomCipher): Promise<Decrypted[]> {
    const out: Decrypted[] = [];
    // Ratchet decryption is stateful and order-sensitive, so process serially.
    for (const wire of wires) {
      try {
        out.push({ wire, env: await c.openMessage(wire) });
      } catch {
        out.push({ wire });
      }
    }
    return out;
  }

  // Initial history load once unlocked.
  useEffect(() => {
    if (!cipher) return;
    let cancelled = false;
    (async () => {
      const { messages: wires } = await api.listMessages(room.id);
      if (cancelled) return;
      const dec = await decryptAll(wires, cipher);
      setMessages(dec);
      lastSeq.current = wires.at(-1)?.seq;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cipher, room.id]);

  // Live updates over WebSocket.
  useEffect(() => {
    if (!cipher) return;
    const ws = openSocket(room.id, async (wire) => {
      if (lastSeq.current && BigInt(wire.seq) <= BigInt(lastSeq.current)) return;
      lastSeq.current = wire.seq;
      const [dec] = await decryptAll([wire], cipher);
      setMessages((prev) => (prev.some((m) => m.wire.id === wire.id) ? prev : [...prev, dec]));
    });
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cipher, room.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  function persistHandle(h: string) {
    setHandle(h);
    localStorage.setItem(`wn_handle_${room.id}`, h);
  }

  if (!cipher) {
    const needsIdentity = room.mode === "pgp" && !pgp.getStoredIdentity();
    return (
      <>
        <LockHeader room={room} locked />
        <div className="messages" style={{ alignItems: "center" }}>
          <form className="card" onSubmit={unlock} style={{ width: 420 }}>
            <h2>Unlock — {CIPHER_LABELS[room.mode]}</h2>
            <UnlockHelp mode={room.mode} isInitiator={isInitiator} />

            {getEnrolledMethod() && hasWrapped(unlockScope) && (
              <>
                <button
                  type="button"
                  className="primary"
                  style={{ width: "100%", marginTop: 8 }}
                  onClick={tapToUnlock}
                >
                  {methodLabel()} — tap to unlock
                </button>
                <div className="muted" style={{ textAlign: "center", margin: "10px 0" }}>
                  or use the passphrase
                </div>
              </>
            )}

            {room.mode === "pgp" && <PgpPanel roomId={room.id} />}

            {(room.mode === "psk" || room.mode === "ratchet" || !needsIdentity) && (
              <div className="field" style={{ marginTop: 12 }}>
                <label>
                  {room.mode === "pgp" ? "Your key passphrase" : "Shared passphrase"}
                </label>
                <input
                  type="password"
                  placeholder={room.mode === "pgp" ? "unlocks your private key" : "shared secret"}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  autoFocus
                />
              </div>
            )}
            {getEnrolledMethod() && !hasWrapped(unlockScope) && room.mode !== "pgp" && (
              <p className="muted" style={{ fontSize: 11, margin: "0 0 8px" }}>
                First time: unlock with your passphrase, then hit <b>🔑 Bind key</b> in the header.
                After that it's tap-only.
              </p>
            )}
            {unlockError && <p className="error">{unlockError}</p>}
            <button
              className="primary"
              type="submit"
              style={{ width: "100%" }}
              disabled={room.mode !== "pgp" && !passphrase}
            >
              Unlock
            </button>
            {!getEnrolledMethod() && (
              <p className="muted" style={{ fontSize: 11, marginBottom: 0 }}>
                Tip: enroll a security key via <b>🔑 Keys</b> (top right) to unlock with a tap.
              </p>
            )}
          </form>
        </div>
      </>
    );
  }

  const visibleMessages = expiry
    ? messages.filter((m) => Date.now() - new Date(m.wire.created_at).getTime() < expiry * 1000)
    : messages;

  return (
    <>
      <LockHeader room={room}>
        <label className="muted" style={{ fontSize: 12 }}>handle:</label>
        <input
          style={{ width: 140 }}
          value={handle}
          onChange={(e) => persistHandle(e.target.value)}
          title="Self-asserted display name, encrypted inside each message"
        />
        <ExpiryControl
          roomId={room.id}
          value={expiry}
          onChange={(updated) => {
            setExpiry(updated.expiry_seconds);
            onRoomUpdate?.(updated);
          }}
        />
        {getEnrolledMethod() && !hasWrapped(unlockScope) && passphrase && (
          <button className="ghost" onClick={bindKey} title="Bind this passphrase to your key for tap-unlock">
            🔑 Bind key
          </button>
        )}
        {bindMsg && (
          <span className="muted" style={{ fontSize: 11 }}>{bindMsg}</span>
        )}
        {room.mode === "pgp" && (
          <button className="ghost" onClick={() => setShowKeys((v) => !v)}>Keys</button>
        )}
        <button
          className="ghost"
          onClick={() => {
            sessionStorage.removeItem(pskKey);
            setCipher(null);
          }}
          title="Forget the key (lock)"
        >
          Lock
        </button>
      </LockHeader>

      {showKeys && room.mode === "pgp" && (
        <div style={{ padding: 16, borderBottom: "1px solid var(--border)" }}>
          <PgpPanel roomId={room.id} />
        </div>
      )}

      <div className="messages">
        {visibleMessages.map((m) => (
          <MessageItem key={m.wire.id} m={m} mine={m.wire.sender_id === user.id} />
        ))}
        <div ref={endRef} />
      </div>

      <Composer roomId={room.id} cipher={cipher} handle={handle} />
    </>
  );
}

function LockHeader({
  room,
  locked,
  children,
}: {
  room: Room;
  locked?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="room-header">
      <span className={`lock ${locked ? "locked" : ""}`}>{locked ? "🔒" : "🔓"}</span>
      <div>
        <div>{room.label || "(unlabeled room)"}</div>
        <div className="muted mono" style={{ fontSize: 11 }}>
          {room.id} · {CIPHER_LABELS[room.mode]}
        </div>
      </div>
      <div className="spacer" />
      {children}
    </div>
  );
}

function UnlockHelp({ mode, isInitiator }: { mode: Room["mode"]; isInitiator: boolean }) {
  if (mode === "psk") {
    return (
      <p className="muted" style={{ marginTop: -8 }}>
        The passphrase (PSK) is shared out of band and never leaves your browser. A wrong
        passphrase simply renders messages as garbage.
      </p>
    );
  }
  if (mode === "ratchet") {
    return (
      <p className="muted" style={{ marginTop: -8 }}>
        Forward secrecy via a double ratchet, bootstrapped from a shared secret you both type
        in. You are the <b>{isInitiator ? "initiator" : "responder"}</b> (decided by who created
        the room). Pairwise (2-party) only; clearing local state requires both peers to reset.
      </p>
    );
  }
  return (
    <p className="muted" style={{ marginTop: -8 }}>
      Encrypt to your peers' OpenPGP public keys, signed by your key. Add each peer's public
      key below and share yours with them, then unlock with your key passphrase.
    </p>
  );
}

function MessageItem({ m, mine }: { m: Decrypted; mine: boolean }) {
  const [showRaw, setShowRaw] = useState(false);
  const time = new Date(m.wire.created_at).toLocaleString();
  const html = useMemo(() => (m.env ? renderMarkdown(m.env.body) : ""), [m.env]);

  if (!m.env) {
    return (
      <div className={`msg undecryptable ${mine ? "mine" : ""}`}>
        <div className="meta"><span>⚠ undecryptable</span><span>{time}</span></div>
        <div className="body">{m.wire.ciphertext.slice(0, 120)}…</div>
      </div>
    );
  }

  const env = m.env;
  return (
    <div className={`msg ${mine ? "mine" : ""}`}>
      <div className="meta">
        <span className="handle">{env.handle || "anon"}</span>
        <span>{time}</span>
      </div>
      {env.file && <FileChip file={env.file} />}
      {env.body &&
        (showRaw ? (
          <pre className="body mono">{env.body}</pre>
        ) : (
          <div className="body" dangerouslySetInnerHTML={{ __html: html }} />
        ))}
      {env.body && (
        <div className="actions">
          <button onClick={() => setShowRaw((v) => !v)}>{showRaw ? "Rendered" : "Raw"}</button>
          <button onClick={() => navigator.clipboard.writeText(env.body)}>Copy</button>
        </div>
      )}
    </div>
  );
}

function FileChip({ file }: { file: FileRef }) {
  const [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    try {
      const ct = await api.downloadFile(file.id);
      const plain = await decryptFile(file.key, file.iv, ct);
      const blob = new Blob([plain], { type: file.mime || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Could not decrypt this file.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="file-chip">
      <span>📎</span>
      <span>{file.name}</span>
      <span className="muted">({Math.max(1, Math.round(file.size / 1024))} KB)</span>
      <button onClick={download} disabled={busy}>{busy ? "…" : "Download"}</button>
    </div>
  );
}

// Per-room "disappearing messages" control. Changing it deletes/keeps messages
// server-side; the presets cover the common cases, and a non-preset value (set
// elsewhere) is shown as an extra read-back option so it isn't silently lost.
const EXPIRY_OPTIONS: { label: string; value: number | null }[] = [
  { label: "No expiry", value: null },
  { label: "Expire: 24h", value: 60 * 60 * 24 },
  { label: "Expire: 7d", value: 60 * 60 * 24 * 7 },
  { label: "Expire: 30d", value: 60 * 60 * 24 * 30 },
];

function ExpiryControl({
  roomId,
  value,
  onChange,
}: {
  roomId: string;
  value: number | null;
  onChange: (room: Room) => void;
}) {
  const [busy, setBusy] = useState(false);
  const known = EXPIRY_OPTIONS.some((o) => o.value === value);

  async function change(v: number | null) {
    setBusy(true);
    try {
      const { room } = await api.setRoomExpiry(roomId, v);
      onChange(room);
    } catch (e) {
      alert("Couldn't change expiry: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <select
      className="expiry"
      disabled={busy}
      value={value === null ? "null" : String(value)}
      onChange={(e) => change(e.target.value === "null" ? null : Number(e.target.value))}
      title="Messages older than this are permanently deleted from the server"
    >
      {!known && value !== null && <option value={String(value)}>{`Expire: ${value}s`}</option>}
      {EXPIRY_OPTIONS.map((o) => (
        <option key={String(o.value)} value={o.value === null ? "null" : String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Composer({ roomId, cipher, handle }: { roomId: string; cipher: RoomCipher; handle: string }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function send(file?: FileRef) {
    if (!text.trim() && !file) return;
    setBusy(true);
    setError(null);
    try {
      const env: Envelope = { v: 1, handle, body: text, file, ts: Date.now() };
      const { iv, ciphertext } = await cipher.sealMessage(env);
      await api.postMessage(roomId, iv, ciphertext);
      setText("");
    } catch (e) {
      setError("Send failed: " + (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Encrypt a file, upload the ciphertext, then post a message referencing it.
  // Shared by the file picker and clipboard paste.
  async function uploadAndSend(f: File) {
    setBusy(true);
    setError(null);
    try {
      const enc = await encryptFile(await f.arrayBuffer());
      const { fileId } = await api.uploadFile(roomId, enc.bytes);
      const fileRefObj: FileRef = {
        id: fileId,
        name: f.name || "pasted-image.png",
        mime: f.type,
        size: f.size,
        key: enc.key,
        iv: enc.iv,
      };
      await send(fileRefObj);
    } catch (err) {
      setError("Attachment failed: " + (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) void uploadAndSend(f);
  }

  // Paste an image straight from the clipboard (screenshots, copied images).
  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const file = Array.from(e.clipboardData?.items ?? [])
      .find((it) => it.kind === "file" && it.type.startsWith("image/"))
      ?.getAsFile();
    if (file) {
      e.preventDefault();
      void uploadAndSend(file);
    }
  }

  return (
    <div className="composer">
      {error && <p className="error" style={{ margin: 0 }}>{error}</p>}
      <div className="row">
        <textarea
          rows={3}
          placeholder="Write markdown… Enter to send, Shift+Enter for a new line. Paste an image to attach."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={onPaste}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter inserts a newline. Ignore Enter during
            // IME composition so it doesn't fire mid-character.
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button className="primary" onClick={() => send()} disabled={busy}>Send</button>
          <button onClick={() => fileRef.current?.click()} disabled={busy}>📎 File</button>
        </div>
      </div>
      <input ref={fileRef} type="file" hidden onChange={onPickFile} />
    </div>
  );
}
